import { useEffect, useState } from "react";
import {
  Loader2,
  RefreshCw,
  LogOut,
  Phone,
  Mail,
  MessageSquare,
  Clock,
} from "lucide-react";
import LogoMark from "@/components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  sent: "border-steel text-bone",
  failed: "border-red-800 text-red-300",
  pending: "border-bone/20 text-bone/50",
};

export default function RequestsDashboard({ onLogout }) {
  const [requests, setRequests] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/admin/requests`, {
        credentials: "include",
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      const data = await res.json();
      setRequests(data.requests || []);
    } catch {
      setRequests([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
    onLogout();
  };

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-10 sm:px-8">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-bone/10 pb-6">
        <div className="flex items-center gap-3">
          <LogoMark className="h-11" />
          <div>
            <h1 className="font-display text-3xl font-black uppercase tracking-[0.02em] text-bone sm:text-4xl">
              Requests Dashboard
            </h1>
            <p
              data-testid="admin-requests-count"
              className="text-xs font-semibold uppercase tracking-[0.2em] text-bone/50"
            >
              {requests
                ? `${requests.length} submission${requests.length === 1 ? "" : "s"}`
                : "Loading..."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="admin-requests-refresh-button"
            className="flex items-center gap-2 border border-bone/20 px-4 py-2 font-display text-lg font-semibold uppercase tracking-wide text-bone transition-colors duration-200 hover:border-bone disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 bg-navy px-4 py-2 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-bone hover:text-ink"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </header>

      {requests === null ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-steel" />
        </div>
      ) : requests.length === 0 ? (
        <p
          data-testid="admin-requests-empty"
          className="py-24 text-center font-display text-2xl uppercase tracking-[0.02em] text-bone/50"
        >
          No requests yet.
        </p>
      ) : (
        <ul data-testid="admin-requests-list" className="mt-8 grid gap-4">
          {requests.map((r) => (
            <li
              key={r.id}
              data-testid="admin-request-card"
              className="border border-bone/10 bg-charcoal p-5 sm:p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-2xl font-bold uppercase tracking-[0.02em] text-bone">
                    {r.first_name} {r.last_name}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-bone/75">
                    <a
                      href={`tel:${r.phone}`}
                      data-testid="admin-request-phone-link"
                      className="flex items-center gap-1.5 transition-colors hover:text-bone"
                    >
                      <Phone className="h-3.5 w-3.5 text-steel" />
                      {r.phone}
                    </a>
                    {r.email && (
                      <a
                        href={`mailto:${r.email}`}
                        data-testid="admin-request-email-link"
                        className="flex items-center gap-1.5 break-all transition-colors hover:text-bone"
                      >
                        <Mail className="h-3.5 w-3.5 shrink-0 text-steel" />
                        {r.email}
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    className={`border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${
                      STATUS_STYLES[r.email_status] || STATUS_STYLES.pending
                    }`}
                  >
                    {r.email_status || "pending"}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-bone/50">
                    <Clock className="h-3 w-3" />
                    {r.created_at
                      ? new Date(r.created_at).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : ""}
                  </span>
                </div>
              </div>
              <p className="mt-4 flex items-start gap-2 border-t border-bone/10 pt-4 text-sm leading-relaxed text-bone/85">
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-steel" />
                {r.message}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
